// Phase 17 Plan 02 Task 1 — Cube primitive.
//
// Letter-cube geometry primitive consuming the `.lcube` recipe in
// apps/web/src/styles/pieces.css. The CSS recipe handles 3D chamfer,
// size variants (default / small / tiny), and state mods (traced /
// frozen / drop / pulse / back). This component is a strict-typed
// React shell that composes className + ARIA + (for `drop`)
// animation-delay only.
//
// Recipe source: apps/web/src/styles/pieces.css `.lcube` (take-two re-skin).
//
// ARIA contract (UI-SPEC §5.1 row 1): when the cube is `traced` OR
// `frozen` AND a `letter` is provided, the cube is a meaningful
// glyph — expose it as role="img" with the letter as aria-label.
// Otherwise the cube is decorative chrome (a back-face or an empty
// shared-letter slot) and gets no role.

import type { CSSProperties, JSX } from 'react'

export interface CubeProps {
  letter?: string
  size?: 'default' | 'small' | 'tiny'
  traced?: boolean
  frozen?: boolean
  drop?: boolean
  dropDelay?: number
  pulse?: boolean
  back?: boolean
  style?: CSSProperties
}

export function Cube({
  letter,
  size = 'default',
  traced = false,
  frozen = false,
  drop = false,
  dropDelay,
  pulse = false,
  back = false,
  style,
}: CubeProps): JSX.Element {
  const classes = [
    'lcube',
    size !== 'default' ? size : null,
    back ? 'back' : null,
    traced ? 'traced' : null,
    frozen ? 'frozen' : null,
    drop ? 'drop' : null,
    pulse ? 'pulse' : null,
  ]
    .filter(Boolean)
    .join(' ')

  const isLabeled = (traced || frozen) && Boolean(letter)
  const computedStyle: CSSProperties = {
    ...(drop && dropDelay !== undefined
      ? { animationDelay: `${dropDelay}ms` }
      : {}),
    ...style,
  }

  return (
    <div
      className={classes}
      style={computedStyle}
      {...(isLabeled ? { role: 'img', 'aria-label': letter } : {})}
    >
      {!back && letter ? letter : null}
    </div>
  )
}
